import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import SectionBlock from "./Draggable/Block/SectionBlock";
import DividerBlock from "./Draggable/Block/DividerBlock";
import ButtonBlock from "./Draggable/Block/ButtonBlock";
import { useLocation, useNavigate } from "react-router-dom";

import TextBlock from "./Draggable/Block/TextBlock";
import ImageBlock from "./Draggable/Block/ImageBlock";
import InputBlock from "./Draggable/Block/InputBlock";
import { useApp } from "../../context/AppState";
import { useAction } from "../../hooks/useAction";
import DraggableWrapper from "../Draggable";
import {
  POPUP_SECTIONS,
  settingIndex,
} from "../../constant/constant";
import {
  getDraggablePopupBackgroundStyle,
  sentPostMessage,
} from "../../helper";
import TimerBlock from "./Draggable/Block/TimerBlock";
import ProductBlock from "./Draggable/Block/ProductBlock";
import VideoBlock from "./Draggable/Block/VideoBlock";
import CountdownBlock from "./Draggable/Block/CountdownBlock";
import SliderBlock from "./Draggable/Block/SliderBlock";
import CustomHtmlBlock from "./Draggable/Block/CustomHtmlBlock";
import ChoiceBlock from "./Draggable/Block/ChoiceBlock";
import ImageCarousel from "./Draggable/Block/ImageCarousel";
import Draggable from "react-draggable";
import FreeShippingProgress from "./Draggable/Block/FreeShippingProgress";
import ShippingProtection from "./Draggable/Block/ShippingProtection";
import CartItemBlockNew from "./Draggable/Block/CartItemBlockNew";
import PopupWatermark from "./PopupWatermark";
import { shouldShowPopupWatermark } from "../../utils/popup-watermark";
import {
  computeStepContentBounds,
  getStepSectionsForPopup,
  parseDimensionValue,
  stripShellDimensionsFromCustomCSS,
} from "../../utils/popup-step-sections";
import {
  isCartDrawerEditorLayoutLocked,
  isCartDrawerLiveFluidLayout,
  isCartProductOfferPopup,
} from "../../utils/cartDrawerRuntime";
import {
  ackDeselectToParent,
  ackSelectBlockToParent,
  isDeselectBlockMessage,
  parseSelectBlockMessage,
} from "../../utils/preview-selection-messages";
import { PREVIEW_BLOCK_HIGHLIGHT } from "../../utils/preview-block-highlight";
import { usesCatalogPopupLayout } from "../../popup-layouts/index.js";

const RenderDraggablePopup = ({ popupData, popupState, setPopupState }) => {
  const {
    appState,
    isMobile,
    clickTriggerEvents,
    setClickTriggerEvents,
    popupGlobalData,
  } = useApp();

  const popupRef = useRef(null);
  const popupOverlayRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const activeStep = useMemo(() => {
    const fromPopupState = popupState?.activeStep;
    if (typeof fromPopupState === "number" && !Number.isNaN(fromPopupState)) {
      return fromPopupState;
    }
    if (
      appState?.is_preview_mode &&
      typeof appState?.activeStep === "number" &&
      !Number.isNaN(appState.activeStep)
    ) {
      return appState.activeStep;
    }
    return 0;
  }, [popupState?.activeStep, appState?.is_preview_mode, appState?.activeStep]);
  const popupType = useMemo(() => popupData?.popUpType, [popupData]);
  const popupId = useMemo(() => popupData?._id, [popupData]);
  const currentParentData = useMemo(() => popupGlobalData?.[`${popupId}_${popupType}`] ?? {}, [popupGlobalData]);
  const [selectedBlock, setSelectedBlock] = useState({});
  const selectedBlockRef = useRef(selectedBlock);
  selectedBlockRef.current = selectedBlock;

  const handleBlockClick = useCallback(
    (type, index) => {
      if (!appState?.is_preview_mode) return;
      const prev = selectedBlockRef.current;
      if (prev.type === type && prev.index === index) return;
      ackSelectBlockToParent({ type, index });
      setSelectedBlock({ type, index });
    },
    [appState?.is_preview_mode]
  );

  const handleOutSideBlockClick = useCallback(() => {
    setSelectedBlock({});
    if (appState?.is_preview_mode) {
      ackDeselectToParent();
    }
  }, [appState?.is_preview_mode]);

  const openSettingBar = useCallback(
    (index) => {
      setSelectedBlock({});
      if (appState?.is_preview_mode) {
        ackSelectBlockToParent({ index });
      }
    },
    [appState?.is_preview_mode]
  );

  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      if (isDeselectBlockMessage(data)) {
        setSelectedBlock({});
        return;
      }

      if (data.event === "sectionCreated") {
        const { type, index } = data;
        if (index === -123) {
          handleOutSideBlockClick();
        } else if (typeof index === "number" && index !== -123) {
          setSelectedBlock({ type, index });
          handleBlockClick(type, index);
        }
        return;
      }

      const selection = parseSelectBlockMessage(data);
      if (!selection) return;

      const { type, index, subIndex } = selection;
      if (index === -123 || index === settingIndex) {
        handleOutSideBlockClick();
        return;
      }

      // Product subsections manage their own selection state.
      if (typeof subIndex === "number" && subIndex >= 0) {
        return;
      }

      setSelectedBlock({ type, index });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handleBlockClick, handleOutSideBlockClick]);

  const handleClose = useCallback(
    async (callAddToCartEvents = true) => {

      setPopupState((prev) => ({
        ...prev,
        [`${popupType}_${popupId}`]: {
          ...prev?.[`${popupType}_${popupId}`],
          openPopup: false,
        },
      }));

      if (location?.hash !== "") {
        navigate(`${location?.pathname}`);
      }


      if (
        popupData?.generalSettings?.condition?.product?.onCartAdd &&
        popupData?.generalSettings?.condition?.product
          ?.onCartAddEventPrevented &&
        callAddToCartEvents
      ) {
        // setTimeout(() => {
        //     if (addToCartEvents && typeof addToCartEvents === 'function') {
        //       addToCartEvents();
        //     }
        //   setTimeout(() => {
        //     setAddToCartEvents(null);
        //   }, 100);
        // }, 100);

        setTimeout(() => {
          if (window.weupsellProceedOriginal && typeof window.weupsellProceedOriginal === 'function') {
            window.weupsellProceedOriginal();
          }
        setTimeout(() => {
          window.weupsellProceedOriginal = null;
        }, 100);
      }, 100);
      }

      if (
        clickTriggerEvents?.[`${popupType}-${popupId}`] &&
        callAddToCartEvents &&
        typeof clickTriggerEvents?.[`${popupType}-${popupId}`] === "function"
      ) {
        clickTriggerEvents?.[`${popupType}-${popupId}`]();
        setTimeout(() => {
          setClickTriggerEvents((prev) => {
            const temp = prev;
            delete temp[`${popupType}-${popupId}`];
            return temp;
          });
        }, 100);
      }
    },
    [setPopupState, location, navigate, popupType, popupId]
  );

  const sectionsSwitch = useCallback(
    (section, index) => {
      switch (section?.type) {
        case POPUP_SECTIONS.SECTION:
          return <SectionBlock section={section} />;
        case POPUP_SECTIONS.DIVIDER:
          return <DividerBlock section={section} />;
        case POPUP_SECTIONS.CUSTOM_HTML:
          return <CustomHtmlBlock section={section} parentData={currentParentData}/>;
        case POPUP_SECTIONS.CHOICE:
          return <ChoiceBlock section={section} />;
        case POPUP_SECTIONS.TITLE:
        case POPUP_SECTIONS.SUBTITLE:
        case POPUP_SECTIONS.PARAGRAPH:
        case POPUP_SECTIONS.CODE_SUMMARY:
        case POPUP_SECTIONS.TEXT:
        case POPUP_SECTIONS.DESCRIPTION:
          return (
            <TextBlock
              popupData={popupData}
              activeStep={activeStep}
              index={index}
              section={section}
              parentData={currentParentData}
            />
          );
        case POPUP_SECTIONS.COVER_IMAGE:
          return (
            <ImageBlock
              section={section}
              setPopupState={setPopupState}
              handleClose={handleClose}
              popupData={popupData}
              popupState={popupState}
              parentData={currentParentData}
            />
          );
        case POPUP_SECTIONS.VIDEO:
          return <VideoBlock section={section} />;
        case POPUP_SECTIONS.BUTTON:
          return (
            <ButtonBlock
              popupData={popupData}
              popupState={popupState}
              setPopupState={setPopupState}
              section={section}
              handleClose={handleClose}
              parentData={currentParentData}
            />
          );
        case POPUP_SECTIONS.INPUT:
          return <InputBlock section={section} />;
        case POPUP_SECTIONS.COUNTDOWN:
          return <CountdownBlock popupData={popupData} section={section} />;
        case POPUP_SECTIONS.VIDEO_SLIDER:
          return (
            <SliderBlock
              popupData={popupData}
              section={section}
              handleClose={handleClose}
              popupState={popupState}
              setPopupState={setPopupState}
            />
          );
        case POPUP_SECTIONS.IMAGE_CAROUSEL:
          return (
            <ImageCarousel
              popupData={popupData}
              section={section}
              handleClose={handleClose}
            />
          );
        case POPUP_SECTIONS.PRODUCT:
          return (
            <ProductBlock
              popupData={popupData}
              popupState={popupState}
              setPopupState={setPopupState}
              section={section}
              activeStep={activeStep}
              index={index}
              handleClose={handleClose}
            />
          );
          case POPUP_SECTIONS.FREESHIPPINGPROGRESS:
          return(
            <FreeShippingProgress
            popupData={popupData}
            popupState={popupState}
            setPopupState={setPopupState}
            section={section}
            activeStep={activeStep}
            index={index}
            handleClose={handleClose}
            />
          );
        case POPUP_SECTIONS.CARTITEMBLOCK:
          return (
            <CartItemBlockNew
              popupData={popupData}
              popupState={popupState}
              setPopupState={setPopupState}
              section={section}
              activeStep={activeStep}
              index={index}
              handleClose={handleClose}
            />
          );
            case POPUP_SECTIONS.SHIPPINGPROTECTION:
              return (
                <ShippingProtection
                  popupData={popupData}
                  popupState={popupState}
                  setPopupState={setPopupState}
                  section={section}
                  activeStep={activeStep}
                  index={index}
                  handleClose={handleClose}
                />
              );
            
        default:
          return null;
      }
    },
    [popupData, popupState, setPopupState, activeStep]
  );

  const [maxDimensions, setMaxDimensions] = useState({ width: 0, height: 0 });

  const isCartDrawerEmbed = useMemo(
    () => isCartProductOfferPopup(popupData),
    [popupData]
  );

  const isCartDrawerLiveFluid = useMemo(
    () => isCartDrawerLiveFluidLayout(popupData, appState?.is_preview_mode),
    [popupData, appState?.is_preview_mode]
  );

  const isCartDrawerShellFullWidth = useMemo(
    () => isCartDrawerEmbed && !appState?.is_preview_mode,
    [isCartDrawerEmbed, appState?.is_preview_mode]
  );

  const lockCartDrawerSectionsInEditor = useMemo(
    () => isCartDrawerEditorLayoutLocked(popupData, appState?.is_preview_mode),
    [popupData, appState?.is_preview_mode]
  );

  const fluidCanvasRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (popupData && popupState) {
      setIsLoaded(true);
    }
  }, [popupData, popupState]);

  const width =
    popupData?.schema[activeStep]?.popupSettings?.customCSS?.[
      isMobile ? "mobile" : "desktop"
    ]?.width ?? 0;
  const height =
    popupData?.schema[activeStep]?.popupSettings?.customCSS?.[
      isMobile ? "mobile" : "desktop"
    ]?.height ?? 0;

  const widthUnit =
    popupData?.schema[activeStep]?.popupSettings?.customCSS?.[
      isMobile ? "mobile" : "desktop"
    ]?.widthUnit ?? "px";
  const heightUnit =
    popupData?.schema[activeStep]?.popupSettings?.customCSS?.[
      isMobile ? "mobile" : "desktop"
    ]?.heightUnit ?? "px";

  const stepSections = useMemo(
    () => getStepSectionsForPopup(popupData, activeStep),
    [popupData, activeStep]
  );

  const stepViewCss = useMemo(() => {
    const raw =
      popupData?.schema?.[activeStep]?.popupSettings?.customCSS?.[
        isMobile ? "mobile" : "desktop"
      ] ?? {};
    return stripShellDimensionsFromCustomCSS(raw);
  }, [popupData, activeStep, isMobile]);

  useEffect(() => {
    const childDimensions = stepSections.map((section) => ({
      width: parseDimensionValue(
        section?.setting?.dimension?.[isMobile ? "mobile" : "desktop"]?.width
      ),
      height: parseDimensionValue(
        section?.setting?.dimension?.[isMobile ? "mobile" : "desktop"]?.height
      ),
    }));

    let maxWidth = 0;
    let maxHeight = 0;
    if (childDimensions.length) {
      maxWidth = Math.max(...childDimensions.map((dim) => dim.width));
      maxHeight = Math.max(...childDimensions.map((dim) => dim.height));
    }

    const bounds = computeStepContentBounds(stepSections, isMobile);

    setMaxDimensions({
      width: width > 0 ? width : Math.max(maxWidth, bounds.width),
      height: height > 0 ? height : Math.max(maxHeight, bounds.height),
    });
  }, [popupData, width, height, activeStep, isMobile, stepSections]);

  const designCanvasWidth = maxDimensions.width;

  useEffect(() => {
    if (
      !isCartDrawerLiveFluid ||
      !designCanvasWidth ||
      !popupRef.current ||
      !fluidCanvasRef.current
    ) {
      return;
    }

    const updateFluidScale = () => {
      const shellWidth = popupRef.current?.offsetWidth ?? 0;
      if (!shellWidth) return;

      const scaleX = shellWidth / designCanvasWidth;
      const canvas = fluidCanvasRef.current;
      canvas.style.width = `${designCanvasWidth}px`;
      canvas.style.transform = `scaleX(${scaleX})`;
      canvas.style.transformOrigin = "top left";
    };

    updateFluidScale();
    const observer = new ResizeObserver(updateFluidScale);
    observer.observe(popupRef.current);
    return () => observer.disconnect();
  }, [isCartDrawerLiveFluid, designCanvasWidth, isLoaded]);

  const showWatermark = useMemo(
    () =>
      shouldShowPopupWatermark(
        popupData,
        activeStep,
        appState?.watermark,
        appState?.is_preview_mode,
        appState?.shop
      ),
    [
      popupData,
      activeStep,
      appState?.watermark,
      appState?.is_preview_mode,
      appState?.shop,
    ]
  );

    // Auto-focus product block once per editor step — NOT on every UPDATE_PROPS/schema refresh.
    const productAutoFocusStepRef = useRef(null);

    useEffect(() => {
      productAutoFocusStepRef.current = null;
    }, [activeStep]);

    useEffect(() => {
      if (!appState?.is_preview_mode) return;
      if (productAutoFocusStepRef.current === activeStep) return;

      const initialFocusIndex = stepSections.findIndex(
        (section) =>
          section.type === POPUP_SECTIONS.OFFER_PRODUCT ||
          section.type === POPUP_SECTIONS.PRODUCT
      );

      if (initialFocusIndex < 0) return;

      productAutoFocusStepRef.current = activeStep;
      handleBlockClick(
        stepSections[initialFocusIndex]?.type,
        initialFocusIndex
      );
    }, [activeStep, stepSections, appState?.is_preview_mode, handleBlockClick]);


    useEffect(()=>{
      const latestShadowRootContainer = document.querySelector("#popup-preview-shadow-root");

      if (latestShadowRootContainer) {
        latestShadowRootContainer.style.zIndex = `${popupData?.schema?.[activeStep]?.popupSettings?.zIndex ?? 9999999999999}`;
      }
      
    },[activeStep])

  useEffect(() => {
    if (!appState?.is_preview_mode) return undefined;

    const handleOutsideClick = (event) => {
      if (
        event.target.closest("[data-draggable-toolbar]") ||
        event.target.closest("[data-draggable-icon]") ||
        event.target.closest('[title="Duplicate"]') ||
        event.target.closest('[title="Lock element position"]') ||
        event.target.closest('[title="Unlock element position"]') ||
        event.target.closest('[title="Delete"]') ||
        event.target.closest('[title="Layers"]')
      ) {
        return;
      }

      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleOutSideBlockClick();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [appState?.is_preview_mode, handleOutSideBlockClick]);

  useEffect(() => {
    if (!appState?.is_preview_mode) return;
    if (!popupOverlayRef.current) return;

    if (Object.keys(selectedBlock).length === 0) {
      popupOverlayRef.current.style.opacity = "1";
    } else {
      popupOverlayRef.current.style.opacity = "0";
    }
  }, [
    isLoaded,
    popupOverlayRef.current,
    selectedBlock,
    appState?.is_preview_mode,
  ]);

  const shellWidth = isCartDrawerShellFullWidth
    ? "100%"
    : `${maxDimensions?.width}${widthUnit ?? "px"}`;
  const shellHeight = `${maxDimensions?.height}${heightUnit ?? "px"}`;
  const shellTransform = isCartDrawerShellFullWidth
    ? "none"
    : widthUnit === "%" || heightUnit === "%"
      ? "none"
      : `translate(-50%, -50%) translate(${maxDimensions.width / 2}px, ${
          maxDimensions.height / 2
        }px)`;

  const usesStackedCatalogLayout = usesCatalogPopupLayout(popupData);

  const handleCanvasBackgroundClick = useCallback(
    (e) => {
      if (!appState?.is_preview_mode || e.target !== e.currentTarget) return;
      handleOutSideBlockClick();
    },
    [appState?.is_preview_mode, handleOutSideBlockClick]
  );

  const renderDraggablePopup = useMemo(() => {
    if (!isLoaded) return null;
    return (
      <>
        {appState?.is_preview_mode && (
          <div
            style={{
              position: "absolute",
              boxSizing: "border-box",
              zIndex: 9999,
              opacity: 0,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: PREVIEW_BLOCK_HIGHLIGHT.shellGuideBorderColor,
              borderRadius: 0,
              background: "transparent",
              backgroundColor: "transparent",
              pointerEvents: "none",
              ...maxDimensions,
              width: isCartDrawerShellFullWidth
                ? "100%"
                : `calc(${maxDimensions?.width}${widthUnit ?? "px"}`,
              height: `calc(${maxDimensions?.height}${heightUnit ?? "px"}`,
              transform: isCartDrawerShellFullWidth
                ? "none"
                : widthUnit === "%" || heightUnit === "%"
                  ? `scale(${isMobile ? 1.04 : 1.15})`
                  : `translate(calc(-50%), calc(-50% )) translate(${
                      maxDimensions.width / 2
                    }px, ${maxDimensions.height / 2}px) scale(${
                      isMobile ? 1.04 : 1.15
                    }) `,
            }}
            ref={popupOverlayRef}
          >
            <div
              style={{
                position: "absolute",
                top: "0px",
                left: "-4px",
                transform: "translateY(-100%)",
                background: PREVIEW_BLOCK_HIGHLIGHT.shellGuideLabelBg,
                color: "#fff",
                padding: "6px 12px",
                borderTopRightRadius: "6px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                zIndex: 9999,
              }}
            >
              Live Preview — Click any element to change value
            </div>
          </div>
        )}
        <Draggable
          disabled={
            appState?.is_preview_mode ||
            !(popupData?.generalSettings?.draggableInLiveMode?.enabled
              ? popupData?.generalSettings?.draggableInLiveMode?.step ===
                activeStep
              : false)
          }
        >
          <div
            className={
              isCartDrawerShellFullWidth
                ? "weupsell-popup-shell weupsell-cart-drawer-embed"
                : "weupsell-popup-shell"
            }
            ref={popupRef}
            style={{
              position: "absolute",
              boxSizing: "border-box",
              overflow: "visible",
              pointerEvents: "none",
              ...maxDimensions,
              width: shellWidth,
              height: shellHeight,
              transform: shellTransform,
            }}
          >
          <div
            style={{
              pointerEvents: "all",
              position: "relative",
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              overflow: "visible",
              ...stepViewCss,
              ...(popupData?.schema?.[activeStep]?.popupSettings?.customCSS?.[
                isMobile ? "mobile" : "desktop"
              ]?.hasOwnProperty("showBackground")
                ? !popupData.schema[activeStep].popupSettings.customCSS[
                    isMobile ? "mobile" : "desktop"
                  ].showBackground
                  ? {
                      background: "transparent",
                      backgroundColor: "transparent",
                    }
                  : {}
                : {}),
              display: widthUnit === "%" ? "flex" : "block",
              justifyContent: widthUnit === "%" ? "center" : "unset",
              alignItems: heightUnit === "%" ? "center" : "unset",
            }}
            className="popup-block-wrapper"
          >
            <div
              style={{
                ...getDraggablePopupBackgroundStyle(
                  popupData?.schema[activeStep]?.popupSettings,
                  isMobile
                ),
                ...(popupData?.schema?.[activeStep]?.popupSettings?.customCSS?.[
                  isMobile ? "mobile" : "desktop"
                ]?.hasOwnProperty("showBackground")
                  ? !popupData.schema[activeStep].popupSettings.customCSS[
                      isMobile ? "mobile" : "desktop"
                    ].showBackground
                    ? {
                        background: "transparent",
                        backgroundColor: "transparent",
                      }
                    : {}
                  : {}),
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                zIndex: 0,
                borderRadius:
                  popupData?.schema[activeStep]?.popupSettings?.customCSS?.[
                    isMobile ? "mobile" : "desktop"
                  ]?.borderRadius,
              }}
            />

            {appState?.is_preview_mode && (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  position: "absolute",
                  cursor: "pointer",
                  // zIndex: 999,
                }}
                onClick={(e) => {
                  // Don't interfere with input field clicks
                  const target = e.target;
                  const isInputField = target.tagName === 'INPUT' || 
                                      target.tagName === 'TEXTAREA' || 
                                      target.tagName === 'SELECT' ||
                                      target.contentEditable === 'true' ||
                                      target.closest('input, textarea, select, [contenteditable]');
                  
                  if (!isInputField) {
                    handleOutSideBlockClick();
                  }
                }}
              />
            )}
            {isCartDrawerEmbed ? (
              <div
                className="weupsell-cart-drawer-layout"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  height: "100%",
                  boxSizing: "border-box",
                }}
              >
                <div
                  ref={fluidCanvasRef}
                  className={
                    isCartDrawerLiveFluid
                      ? "weupsell-cart-drawer-fluid-canvas"
                      : undefined
                  }
                  style={{
                    position: "relative",
                    flex: "1 1 auto",
                    minHeight: 0,
                    width: isCartDrawerLiveFluid ? designCanvasWidth : "100%",
                    boxSizing: "border-box",
                  }}
                  onMouseDown={handleCanvasBackgroundClick}
                >
                  {stepSections.map((section, index) => {
                    return (
                      <DraggableWrapper
                        key={index}
                        index={index}
                        popupSettings={
                          popupData?.schema[activeStep]?.popupSettings
                        }
                        section={section}
                        isSelected={
                          section?.type === selectedBlock?.type &&
                          index === selectedBlock?.index
                        }
                        onClick={() =>
                          handleBlockClick(section?.type, index)
                        }
                        lockDraggable={
                          lockCartDrawerSectionsInEditor ||
                          popupData?.generalSettings?.lockDraggable
                        }
                        sectionLockDraggable={
                          section?.setting?.isLockDraggable
                        }
                        isSliderVerical={
                          section?.type === POPUP_SECTIONS.CARTITEMBLOCK
                        }
                        layoutFlow={usesStackedCatalogLayout}
                      >
                        {sectionsSwitch(section, index)}
                      </DraggableWrapper>
                    );
                  })}
                </div>
                {showWatermark ? (
                  <PopupWatermark
                    popupData={popupData}
                    activeStep={activeStep}
                    visible={showWatermark}
                    variant="cart-drawer"
                  />
                ) : null}
              </div>
            ) : (
              <div
                ref={fluidCanvasRef}
                className={
                  isCartDrawerLiveFluid
                    ? "weupsell-cart-drawer-fluid-canvas"
                    : undefined
                }
                style={{
                  position: "relative",
                  width: isCartDrawerLiveFluid ? designCanvasWidth : "100%",
                  height: usesStackedCatalogLayout ? "100%" : "100%",
                  boxSizing: "border-box",
                  display: usesStackedCatalogLayout ? "flex" : "block",
                  flexDirection: usesStackedCatalogLayout ? "column" : "unset",
                  gap: usesStackedCatalogLayout ? 8 : 0,
                  justifyContent: usesStackedCatalogLayout ? "center" : "unset",
                }}
                onMouseDown={handleCanvasBackgroundClick}
              >
                {stepSections.map((section, index) => {
                  return (
                    <DraggableWrapper
                      key={index}
                      index={index}
                      popupSettings={
                        popupData?.schema[activeStep]?.popupSettings
                      }
                      section={section}
                      isSelected={
                        section?.type === selectedBlock?.type &&
                        index === selectedBlock?.index
                      }
                      onClick={() => handleBlockClick(section?.type, index)}
                      lockDraggable={
                        lockCartDrawerSectionsInEditor ||
                        popupData?.generalSettings?.lockDraggable
                      }
                      sectionLockDraggable={section?.setting?.isLockDraggable}
                      isSliderVerical={
                        section?.type === POPUP_SECTIONS.CARTITEMBLOCK
                      }
                      layoutFlow={usesStackedCatalogLayout}
                    >
                      {sectionsSwitch(section, index)}
                    </DraggableWrapper>
                  );
                })}
              </div>
            )}
          </div>
            {showWatermark && !isCartDrawerEmbed ? (
              <PopupWatermark
                popupData={popupData}
                activeStep={activeStep}
                visible={showWatermark}
              />
            ) : null}
          </div>
        </Draggable>
      </>
    );
  }, [
    isLoaded,
    popupData,
    popupState,
    selectedBlock,
    sectionsSwitch,
    handleBlockClick,
    handleCanvasBackgroundClick,
    getDraggablePopupBackgroundStyle,
    activeStep,
    appState?.is_preview_mode,
    maxDimensions,
    widthUnit,
    heightUnit,
    isMobile,
    showWatermark,
    stepSections,
    stepViewCss,
    isCartDrawerEmbed,
    usesStackedCatalogLayout,
    isCartDrawerShellFullWidth,
    isCartDrawerLiveFluid,
    lockCartDrawerSectionsInEditor,
    designCanvasWidth,
    shellWidth,
    shellHeight,
    shellTransform,
  ]);

  return <>{renderDraggablePopup}</>;
};

export default RenderDraggablePopup;
